// src/pages/SqliteViewer.jsx
import { useMemo, useState } from "react";
import api from "../auth/api";

const DEFAULT_SQL = `
SELECT id, wa_account_id, user_id, template, params_json, status, created_at
FROM job_batches
ORDER BY id DESC
LIMIT 50
`.trim();

function isLikelyJSON(x) {
  if (x == null) return false;
  const s = String(x).trim();
  if (!s) return false;
  if (!/^[\[{].*[\]}]$/.test(s)) return false; // begin/end quick check
  try { JSON.parse(s); return true; } catch { return false; }
}

function prettyJSON(x) {
  try { return JSON.stringify(typeof x === "string" ? JSON.parse(x) : x, null, 2); }
  catch { return String(x ?? ""); }
}

function Cell({ value, col, wrap, formatJSON }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const content = useMemo(() => {
    if (formatJSON && isLikelyJSON(value)) {
      return prettyJSON(value);
    }
    return String(value ?? "");
  }, [value, formatJSON]);

  const isLong = content.length > 220 || content.includes("\n");
  const showPreview = isLong && !expanded;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };

  return (
    <div className="relative group">
      <div
        className={[
          "font-mono text-[12.5px] leading-snug",
          "max-w-[520px] md:max-w-[640px]",
          wrap ? "whitespace-pre-wrap break-words" : "whitespace-pre overflow-x-auto",
          "text-slate-100/90",
          showPreview ? "line-clamp-3" : "",
        ].join(" ")}
        style={{ wordBreak: wrap ? "break-word" : "normal" }}
        title={!isLong ? content : ""}
      >
        {content || "—"}
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-1">
        {isLong && (
          <button
            type="button"
            className="px-2 py-0.5 text-xs rounded bg-slate-700 hover:bg-slate-600"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Ver menos" : "Ver todo"}
          </button>
        )}
        <button
          type="button"
          className="px-2 py-0.5 text-xs rounded bg-slate-700 hover:bg-slate-600"
          onClick={copy}
        >
          {copied ? "¡Copiado!" : "Copiar"}
        </button>
      </div>
    </div>
  );
}

export default function SqliteViewer() {
  const [sql, setSql] = useState(DEFAULT_SQL);
  const [paramsText, setParamsText] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [appliedSql, setAppliedSql] = useState("");

  // UI toggles
  const [wrap, setWrap] = useState(true);
  const [formatJSON, setFormatJSON] = useState(true);

  const run = async () => {
    setErr(""); setLoading(true);
    try {
      let params = {};
      if (paramsText.trim()) params = JSON.parse(paramsText);
      const r = await api.post("/admin/sqlite/query", { sql, params });
      setColumns(r.data.columns || []);
      setRows(r.data.rows || []);
      setAppliedSql(r.data.appliedSql || "");
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
      setColumns([]); setRows([]);
      setAppliedSql("");
    } finally { setLoading(false); }
  };

  const downloadCSV = () => {
    if (!rows.length) return;
    const esc = v => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const head = columns.map(esc).join(",");
    const body = rows.map(row => columns.map(c => esc(row[c])).join(",")).join("\n");
    const csv = head + "\n" + body;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "query.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1200);
  };

  const guessedEmpty = !rows.length && !err && !loading;

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-xl font-semibold">SQLite Viewer (solo lectura)</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex flex-col">
          <label className="font-medium mb-1">SQL (solo SELECT/WITH)</label>
          <textarea
            className="border rounded p-2 font-mono min-h-[180px] bg-slate-900 text-slate-100"
            value={sql}
            onChange={e => setSql(e.target.value)}
            spellCheck={false}
          />
          <small className="text-gray-400 mt-1 block">
            Se fuerza <code className="font-mono">LIMIT</code> si no lo incluyes.
          </small>
        </div>

        <div className="flex flex-col">
          <label className="font-medium mb-1">Parámetros JSON (opcional)</label>
          <textarea
            className="border rounded p-2 font-mono min-h-[180px] bg-slate-900 text-slate-100"
            value={paramsText}
            onChange={e => setParamsText(e.target.value)}
            placeholder='{"wa": 2}'
            spellCheck={false}
          />
          <small className="text-gray-400 mt-1 block">
            Usa <code className="font-mono">:param</code> en el SQL y define {`{"param":"valor"}`} aquí.
          </small>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={run}
          disabled={loading}
          className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-60"
        >
          {loading ? "Ejecutando..." : "Ejecutar"}
        </button>
        <button
          onClick={downloadCSV}
          disabled={!rows.length}
          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-60"
        >
          Descargar CSV
        </button>

        <div className="ml-auto flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={wrap} onChange={e => setWrap(e.target.checked)} />
            Ajuste de línea
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={formatJSON} onChange={e => setFormatJSON(e.target.checked)} />
            Formatear JSON
          </label>
        </div>
      </div>

      {appliedSql && (
        <div className="text-xs text-gray-400">
          SQL aplicado: <code className="font-mono">{appliedSql}</code>
        </div>
      )}

      {err && (
        <div className="px-3 py-2 rounded bg-red-900/30 text-red-200 border border-red-700">
          Error: {err}
        </div>
      )}

      {!!rows.length && (
        <div className="overflow-auto border rounded">
          <table className="min-w-full text-sm bg-slate-900 text-slate-100">
            <thead className="bg-slate-800 sticky top-0 z-10">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="text-left p-2 border-b border-slate-700 font-semibold">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={i % 2 ? "bg-slate-900" : "bg-slate-950"}>
                  {columns.map((c) => (
                    <td key={c} className="p-2 border-b border-slate-800 align-top">
                      <Cell value={r[c]} col={c} wrap={wrap} formatJSON={formatJSON} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {guessedEmpty && (
        <div className="text-gray-500">Sin resultados aún. Ejecuta una consulta.</div>
      )}
    </div>
  );
}
